import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import {
  CreateCustomerRequest,
  CustomerGeneral,
  CustomerIndicatorRequest,
  CustomerReduced,
  ListCustomersReducedQuery,
  UpdateCustomerRequest,
} from './Customers.types';

import {
  deleteCustomer,
  createCustomer,
  updateCustomer,
  updateCustomerLGPD,
  updateCustomerShowLGPD,
  activeCustomer,
  listVehiclesFilter,
  listCustomersReduced,
  getCustomerReduced,
  listVehiclesFilterReduced,
  getVehicleFilterReduced,
  getCustomerGeneral,
  customerIndicatorsGranularity,
  customerIndicators,
  customerCpfCnpjExists,
  customerUpdateCategory,
  customerPortoIsValid,
} from './Customers.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import {
  CustomerVehicle,
  CustomerVehicleReduced,
  CustomerVehiclesFilterParamsReduced,
  ListCustomerVehiclesFilterParams,
} from '../CustomerVehicles/CustomerVehicles.types';
import Authorities from '@/types/Authority';
import { queryClient } from '@/Root';
import { PageResult } from '@/core/request/PageResult';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import replaceWords from '@/utils/functions/replaceWords';
import verifyIfHasVinculed from '@/utils/functions/verifyIfHasVinculed';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { AxiosError } from 'axios';
import { isAppPortocar } from '@/index';

export const QUERY_CUSTOMER_PORTO_IS_VALID = 'customer/porto-is-valid';

export function useCustomerReducedList(
  params?: ListCustomersReducedQuery,
  enabled = true,
) {
  const cache = queryClient.getQueryData(['customers/reduced', { ...params }]);

  return useInfiniteQuery(
    ['customers/reduced', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listCustomersReduced(req);
    },
    {
      enabled: enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useCustomerDetailReduced(customerId?: number) {
  return useQuery(
    ['customer/reduced/detail', { customerId }],
    () => getCustomerReduced(customerId),
    {
      enabled: !!customerId,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCustomerReduced() {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<CustomerReduced>([
        'customer/detail/reduced',
        id,
      ]);

      if (cache?.id === id) return cache;

      const data = await getCustomerReduced(id);
      return data;
    },
    {
      onSuccess(data) {
        queryClient.setQueryData(['customer/detail/reduced', data.id], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCustomerGeneral(id?: number) {
  const cache = queryClient.getQueryData(['customer/detail/general', id]);

  return useQuery(
    ['customer/detail/general', id],
    () => getCustomerGeneral(id),
    {
      enabled:
        !!id && hasPermissions([Authorities.REGISTER_CUSTOMER_VIEW]) && !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCustomerGeneralMutation() {
  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<CustomerGeneral>([
        'customer/detail/general',
        id,
      ]);

      if (cache?.id === id) return cache;

      return getCustomerGeneral(id);
    },
    {
      onSuccess(data, id) {
        queryClient.setQueryData(['customer/detail/general', id], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCustomerReducedQuery(id?: number) {
  const cache = queryClient.getQueryData(['customer/detail/reduced', id]);

  return useQuery(
    ['customer/detail/reduced', id],
    () => getCustomerReduced(id),
    {
      enabled:
        !!id && hasPermissions([Authorities.REGISTER_CUSTOMER_VIEW]) && !cache,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useCreateCustomer() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (obj: CreateCustomerRequest) => createCustomer(obj),
    {
      async onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'customers.notifications.create.success.title',
          }),
          description: intl.formatMessage({
            id: 'customers.notifications.create.success.description',
          }),
        });

        await queryClient.invalidateQueries(['customers']);
        queryClient.removeQueries(['customers/reduced']);
        queryClient.removeQueries(['customers']);
      },

      onError(error) {
        notificationError(error);
      },
    },
  );

  return mutation;
}

export function useUpdateCustomer() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    ({ id, obj }: { id: number; obj: UpdateCustomerRequest }) =>
      updateCustomer(id, obj),
    {
      async onSuccess(data) {
        queryChanel({
          invalidateQueries: [
            ['activity/list'],
            ['customers'],
            ['customers/reduced'],
          ],
          removeQueries: [
            ['customers'],
            ['customers/reduced'],
            ['customer/filter/reduced'],
            ['customer/detail/general', data.id],
            ['customer/detail/reduced', data.id],
            ['customer/detail/general/mutation', data.id],
          ],
        });

        notification.success({
          message: intl.formatMessage({
            id: 'customers.notifications.update.success.title',
          }),
          description: intl.formatMessage({
            id: 'customers.notifications.update.success.description',
          }),
        });
      },

      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes(
                'invalid Brazilian individual taxpayer registry number (CPF)',
              )
                ? 'CPF inválido'
                : err.includes(
                    'invalid Brazilian corporate taxpayer registry number (CNPJ)',
                  )
                ? 'CNPJ inválido'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useUpdateCustomerLGPD() {
  const intl = useIntl().formatMessage;

  const mutation = useMutation(updateCustomerLGPD, {
    async onSuccess() {
      await queryClient.invalidateQueries(['customers']);
      await queryClient.resetQueries(['customers/reduced']);
      queryClient.removeQueries(['customers']);
      queryClient.removeQueries(['customer/detail/general']);
      queryClient.removeQueries(['customer/detail/reduced']);

      notification.success({
        message: intl({
          id: 'workOrders.notification.lgpd.change.success.title',
        }),
        description: intl({
          id: 'workOrders.notification.lgpd.change.success.description',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateCustomerShowLGPD() {
  const mutation = useMutation(
    (customerId: number) => updateCustomerShowLGPD(customerId),
    {
      async onSuccess() {
        await queryClient.invalidateQueries(['customers']);
        await queryClient.resetQueries(['customers/reduced']);
        queryClient.removeQueries(['customers']);
        queryClient.removeQueries(['customer/detail/general']);
        queryClient.removeQueries(['customer/detail/reduced']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteCustomer() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => deleteCustomer(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'customers.notifications.remove.success.title',
        }),
        description: intl.formatMessage({
          id: 'customers.notifications.remove.success.description',
        }),
      });
      queryClient.removeQueries(['customers/reduced']);
      queryClient.invalidateQueries(['customers']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            verifyIfHasVinculed(err) ? replaceWords(err) : err,
          ),
      });
    },
  });

  return mutation;
}

export function useActiveCustomer() {
  const intl = useIntl().formatMessage;
  const mutation = useMutation(
    (customerId: number) => activeCustomer(customerId),
    {
      async onSuccess() {
        message.success(intl({ id: 'customer.status.message' }));

        await queryClient.invalidateQueries(['customers']);
        await queryClient.resetQueries(['customers/reduced']);
        queryClient.removeQueries(['customers']);
        queryClient.removeQueries(['customer/detail/general']);
        queryClient.removeQueries(['customer/detail/reduced']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useVehicleFilter(
  params?: ListCustomerVehiclesFilterParams,
  enabled?: boolean,
) {
  const cache = queryClient.getQueryData(['customer/filter', { ...params }]);

  return useQuery(
    ['customer/filter', { ...params }],
    () => listVehiclesFilter(params),
    {
      enabled: enabled && !cache,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useVehiclesFilterReduced(
  params?: CustomerVehiclesFilterParamsReduced,
  enabled = true,
) {
  const queryCLient = useQueryClient();
  const cache = queryCLient.getQueryData([
    'customer/filter/reduced',
    { ...params },
  ]);

  return useInfiniteQuery(
    ['customer/filter/reduced', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listVehiclesFilterReduced(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: enabled && !cache,
      onError(error: AxiosError) {
        if (error.response?.status === 400 && !error.response.data) return;

        notificationError(error);
      },
    },
  );
}

export function useVehicleFilterReducedDetail() {
  const queryCLient = useQueryClient();

  return useMutation(
    async (id: number) => {
      const cache = queryCLient.getQueryData<CustomerVehicleReduced>([
        'vehicle/detail/reduced',
        id,
      ]);

      if (cache?.id === id) return cache;

      return getVehicleFilterReduced(id);
    },
    {
      onSuccess(data) {
        queryCLient.setQueryData(['vehicle/detail/reduced', data.id], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useVehiclesFilterMutation() {
  return useMutation(
    async (params?: ListCustomerVehiclesFilterParams) => {
      const cache = queryClient.getQueryData<PageResult<CustomerVehicle>>([
        'customer/filter',
        { ...params },
      ]);

      if (!!cache) return cache;

      return listVehiclesFilter(params);
    },
    {
      onSuccess(data, vars) {
        queryClient.setQueryData(['customer/filter', vars], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useCustomersIndicators() {
  return useQuery(['customers', 'indicators'], customerIndicators);
}

export function useCustomersIndicatorsGranularity(
  obj?: CustomerIndicatorRequest,
) {
  return useQuery(
    ['customers/indicators', { ...obj }],
    () => customerIndicatorsGranularity(obj),
    {
      enabled: !!(obj?.endDate && obj.initialDate && obj.granularity),
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useCustomerCpfCnpjExists() {
  return useMutation(customerCpfCnpjExists, {
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCustomerUpdateCategory() {
  const queryChanel = useQueryChanel();

  return useMutation(customerUpdateCategory, {
    onSuccess(_, request) {
      queryChanel({
        resetQueries: [
          ['customer/detail/reduced', request.id],
          ['customers'],
          ['customer/detail/general', request.id],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCustomerPortoIsValidData(customerId?: number) {
  return useQuery({
    queryKey: [QUERY_CUSTOMER_PORTO_IS_VALID, { customerId }],
    queryFn: () => customerPortoIsValid(customerId),
    enabled: !!customerId && isAppPortocar,
  });
}
